import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Spinner } from './Spinner';

export function LoginForm() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const { requestMagicLink } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await requestMagicLink(email.trim());
      setIsEmailSent(true);
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTryAgain = () => {
    setIsEmailSent(false);
    setError(null);
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-base-800">
        <div className="max-w-md w-full p-8 text-center bg-base-900 rounded-xl shadow-lg">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 bg-primary-500 text-white">
              <span className="text-2xl">✉️</span>
            </div>
            <h1 className="text-2xl font-semibold mb-2 text-base-100">Check your email</h1>
            <p className="text-base-300">
              We've sent a magic link to <strong className="text-base-100">{email}</strong>
            </p>
          </div>

          <div className="p-4 mb-6 bg-base-800 rounded-lg">
            <p className="text-sm text-base-200">
              Click the link in your email to sign in. The link will expire in 15 minutes.
            </p>
          </div>

          <button
            onClick={handleTryAgain}
            className="w-full px-4 py-2 transition bg-base-700 hover:bg-base-600 text-base-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-base-800">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2 text-base-100">AI Companion</h1>
          <p className="text-base-300">Sign in to continue your conversation</p>
        </div>

        <div className="p-8 bg-base-900 rounded-xl shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-2 text-base-100">
                Email address
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                disabled={isSubmitting}
                className="w-full px-4 py-3 bg-base-800 border border-base-700 rounded-lg text-base-100 placeholder-base-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              />
            </div>

            {error && (
              <div className="p-3 bg-secondary-900 text-secondary-100 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting || !email.trim()}
              className={`w-full px-4 py-3 transition font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed ${
                isSubmitting || !email.trim()
                  ? 'bg-base-700 text-base-400'
                  : 'bg-primary-500 hover:bg-primary-600 text-white'
              }`}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Spinner />
                  Sending magic link...
                </span>
              ) : (
                'Send magic link'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-base-400">
              We'll send you a secure link to sign in without a password.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}